import { useRef, useEffect, useState, useContext, useMemo } from 'react'
import PropTypes from 'prop-types'
import FileList from './file-list'

import Map from 'ol/Map'
import View from 'ol/View'
import TileLayer from 'ol/layer/Tile'
import XYZSource from 'ol/source/XYZ'
import Style from 'ol/style/Style'
import Fill from 'ol/style/Fill'
import Stroke from 'ol/style/Stroke'
import Circle from 'ol/style/Circle'

import PreviewMapFeatureSelect from './preview-map-feature-select'
import PreviewMapLayer from './preview-map-layer'

import UploadFiles from './upload-files'
import FileMetadata from './file-metadata'

import { CreateProjectDispatch } from './create-project'
import LoadingDots from '../ui/shared/loading-dots'
import Blur from '../ui/shared/blur'

import { cost as costPropType } from './prop-types'
import { maxOrthoZoom } from '../../ol-styles'

export default function PreviewFiles({
  files,
  captures: groupedFileList,
  mapboxToken,
  metaData,
  metadataInitializing,
  crops,
  farms,
  farmId,
  fields,
  fieldName,
  crop,
  lastSelected,
  status,
  units,
  projectId,
  projectType,
  options,
  cameraProperties,
  gcpEnabled,
  cost,
  validationMessage,
  isMissingFiles,
  hasMetaDataErrors,
  apiClient,
  stripeToken,
  purchasesEnabled,
  currency,
  allowChanges,
}) {
  const previewFilesMapRef = useRef(null)
  const [map, setMap] = useState(null)
  const [geotaggedLayer, setGeotaggedLayer] = useState(null)
  const dispatch = useContext(CreateProjectDispatch)
  // TODO: could probably just be !metadataInitializing, but I
  // don't have the time to properly test this
  const metaDataInitialized =
    !metadataInitializing || (!metaData && hasMetaDataErrors)

  useEffect(() => {
    const map = new Map({
      target: previewFilesMapRef.current,
      logo: false,
      controls: [],
      layers: [
        new TileLayer({
          source: new XYZSource({
            maxZoom: 17,
            tileSize: [512, 512],
            url:
              'https://api.mapbox.com/styles/v1/mapbox/satellite-streets-v11/tiles/{z}/{x}/{y}?access_token=' +
              mapboxToken,
          }),
        }),
      ],
      view: new View({
        maxZoom: maxOrthoZoom,
        center: [0, 0],
        zoom: 3,
      }) /* 24 corresponds to about 1cm/px */,
    })
    setMap(map)
  }, [mapboxToken, previewFilesMapRef])

  // TODO ONLY Captureid?
  const onSelectImagesInMap = featureCollection => {
    const captureIds = featureCollection.features.map(
      f => f.properties.final_capture_id
    )
    const fileIds = Array.prototype.concat.apply(
      [],
      captureIds.map(captureId => groupedFileList[captureId])
    )
    dispatch({
      type: 'SELECT_FILES',
      payload: fileIds,
    })
    dispatch({
      type: 'SET_LAST_SELECTED',
      payload: captureIds[0],
    })
  }
  const onDeselectImagesInMap = featureCollection => {
    const properties = featureCollection.features[0].properties

    dispatch({
      type: 'UNSELECT_FILES',
      payload: groupedFileList[properties.final_capture_id],
    })

    if (lastSelected === properties.final_capture_id) {
      dispatch({
        type: 'SET_LAST_SELECTED',
        payload: null,
      })
    }
  }

  const lastSelectedCaptureFeatures = useMemo(
    () =>
      lastSelected &&
      metaData?.features?.filter(
        feature => feature.properties.final_capture_id === lastSelected
      ),
    [lastSelected]
  )
  const lastSelectedFiles = useMemo(
    () =>
      lastSelected &&
      groupedFileList[lastSelected].map(fileId => files[fileId]),
    [lastSelected]
  )

  const uploadedFiles = useMemo(
    () =>
      new Set(
        groupedFileList
          ? Object.keys(groupedFileList).filter(key =>
              groupedFileList[key].every(
                fileId => files[fileId].uploadStatus === 'done'
              )
            )
          : []
      ),
    [files, groupedFileList]
  )
  const selectedCaptures = useMemo(() => {
    return new Set(
      groupedFileList
        ? Object.keys(groupedFileList).filter(key =>
            groupedFileList[key].every(fileId => files[fileId].isSelected)
          )
        : []
    )
  }, [files, groupedFileList])

  return (
    <>
      <div className="tw-relative tw--mt-5 tw-custom-border-top">
        <div
          id="map-controls"
          className="tw-absolute tw-top-0 tw-right-0 tw-w-full tw-z-10 tw-mt-5 tw-flex tw-justify-center"></div>
        <div className="tw-flex tw-justify-between tw-h-remove-nav">
          <FileList
            cameraProperties={cameraProperties}
            files={files}
            metaData={metaData}
            metaDataInitialized={metaDataInitialized}
            lastSelected={lastSelected}
            status={status}
            groupedFileList={groupedFileList}
            addFilesLabel={
              isMissingFiles ? 'Add missing images' : 'Add more images'
            }
            allowChanges={allowChanges}
          />
          {!metaDataInitialized && (
            <div className="tw-m-auto">
              <div className="tw-bg-white tw-px-16 tw-py-8 tw-rounded tw-shadow-lg">
                <p className="tw-text-lg tw-text-center tw-mb-8">
                  Please wait, examining images...
                </p>
                <LoadingDots primaryColor />
              </div>
            </div>
          )}
          <div className="tw-flex tw-flex-col tw-justify-between tw-max-w-sm tw-w-1/4 tw-bg-white tw-z-10 tw-p-6 tw-max-h-remove-nav tw-overflow-y-scroll">
            {status === 'success' && <Blur />}
            <FileMetadata
              lastSelectedCaptureFeatures={lastSelectedCaptureFeatures}
              lastSelectedFiles={lastSelectedFiles}
              units={units}
            />
            <UploadFiles
              apiClient={apiClient}
              stripeToken={stripeToken}
              projectId={projectId}
              projectType={projectType}
              crops={crops}
              crop={crop}
              farms={farms}
              farmId={farmId}
              fields={fields}
              fieldName={fieldName}
              files={files}
              metaData={metaData}
              status={status}
              minFiles={3}
              cost={cost}
              units={units}
              mapboxToken={mapboxToken}
              purchasesEnabled={purchasesEnabled}
              currency={currency}
              onFarmChange={farmId =>
                dispatch({ type: 'UPDATE_FARM_ID', payload: farmId })
              }
              onFieldNameChange={fieldName =>
                dispatch({ type: 'UPDATE_FIELD_NAME', payload: fieldName })
              }
              onCropChange={crop =>
                dispatch({ type: 'UPDATE_CROP', payload: crop })
              }
              onUpload={() => dispatch({ type: 'BEGIN_UPLOAD' })}
              options={options}
              onOptionChange={(key, active) =>
                dispatch({ type: 'SET_OPTION', payload: { key, active } })
              }
              validationMessage={validationMessage}
              gcpEnabled={gcpEnabled}
              onAddFiles={files =>
                dispatch({ type: 'ADD_EXTRA_FILES', payload: files })
              }
              onUpdateMetadata={metadata =>
                dispatch({ type: 'UPDATE_METADATA', payload: metadata })
              }
              onPermissionsUpdated={async () => {
                dispatch({
                  type: 'UPDATE_PERMISSIONS',
                  payload: await apiClient.getCurrentPermissions(),
                })
              }}
              setProcessingOption={(option, value) =>
                dispatch({
                  type: 'SET_PROCESSING_OPTION',
                  payload: { key: option, value },
                })
              }
            />
          </div>
        </div>
        <div
          ref={previewFilesMapRef}
          className={`tw-w-full tw-absolute tw-top-0 tw-bottom-0 tw-left-0 tw-right-0 tw-bg-white ${
            !metaDataInitialized ? 'tw-invisible' : ''
          }`}></div>
      </div>
      {map && metaData && (
        <>
          <PreviewMapLayer
            map={map}
            metaData={metaData}
            uploaded={uploadedFiles}
            style={
              new Style({
                image: new Circle({
                  radius: 6,
                  fill: new Fill({ color: 'white' }),
                  stroke: new Stroke({ color: 'black', width: 0.5 }),
                }),
              })
            }
            onLayerCreated={layer => {
              setGeotaggedLayer(layer)
            }}
            onLayerUpdate={layer => {
              setGeotaggedLayer(layer)
            }}
            onLayerRemoved={() => {
              setGeotaggedLayer(null)
            }}
          />
          <PreviewMapFeatureSelect
            map={map}
            lastSelected={lastSelected}
            layer={geotaggedLayer}
            selection={selectedCaptures}
            style={
              new Style({
                image: new Circle({
                  radius: 6,
                  fill: new Fill({ color: '#00A6CC' }),
                  stroke: new Stroke({ color: 'white', width: 2 }),
                }),
              })
            }
            enabled={status !== 'uploading' && status !== 'success'}
            onSelect={onSelectImagesInMap}
            onDeselect={onDeselectImagesInMap}
          />
        </>
      )}
    </>
  )
}

PreviewFiles.propTypes = {
  files: PropTypes.object.isRequired,
  allSelected: PropTypes.bool,
  mapboxToken: PropTypes.string,
  metaData: PropTypes.object,
  metadataInitializing: PropTypes.bool,
  crops: PropTypes.array,
  units: PropTypes.string,
  projectId: PropTypes.number,
  captures: PropTypes.object,
  farms: PropTypes.array,
  farmId: PropTypes.number,
  fields: PropTypes.array,
  fieldName: PropTypes.string,
  crop: PropTypes.string,
  lastSelected: PropTypes.string,
  status: PropTypes.string,
  projectType: PropTypes.string,
  options: PropTypes.array,
  cameraProperties: PropTypes.object,
  gcpEnabled: PropTypes.bool,
  cost: costPropType,
  validationMessage: PropTypes.node,
  isMissingFiles: PropTypes.bool,
  hasMetaDataErrors: PropTypes.bool,
  apiClient: PropTypes.object,
  stripeToken: PropTypes.string,
  purchasesEnabled: PropTypes.bool,
  currency: PropTypes.string,
  allowChanges: PropTypes.bool,
}
